import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BLOG_POSTS } from '../data/blog';
import { ALBERTA_RESTAURANTS, ALBERTA_FOOD_TRUCKS } from '../data/restaurants';
import { setMeta } from '../lib/seo';
import Footer from '../components/Footer';
import NotFound from './NotFound';

const ALL_RESTAURANTS = [...ALBERTA_RESTAURANTS, ...ALBERTA_FOOD_TRUCKS];

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    setMeta({
      title: `${post.title} | Culinera`,
      description: post.metaDescription || post.excerpt,
      canonical: `https://culinera.ca/blog/${post.slug}`,
      image: post.heroImageUrl || (post.heroImage ? `https://culinera.ca/images/${post.heroImage}` : undefined),
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.metaDescription || post.excerpt,
        datePublished: post.publishedAt,
        author: { '@type': 'Organization', name: 'Culinera Editorial' },
        publisher: { '@type': 'Organization', name: 'Culinera', url: 'https://culinera.ca' },
        articleSection: post.category,
        about: post.city,
      },
    });
  }, [post]);

  if (!post) {
    return <NotFound />;
  }

  // Render inline links [text](/path) within a chunk of text.
  const renderInline = (text, keyPrefix) => {
    const segs = text.split(/(\[[^\]]+\]\([^)]+\))/g);
    return segs.map((s, k) => {
      const link = s.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        const [, label, href] = link;
        if (href.startsWith('/')) {
          return (
            <Link key={`${keyPrefix}-${k}`} to={href} className="text-teal-600 font-semibold hover:underline" data-testid={`blog-inline-link-${href.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`}>
              {label}
            </Link>
          );
        }
        return <a key={`${keyPrefix}-${k}`} href={href} className="text-teal-600 font-semibold hover:underline" target="_blank" rel="noopener noreferrer">{label}</a>;
      }
      return <span key={`${keyPrefix}-${k}`}>{s}</span>;
    });
  };

  // Split content into paragraphs; render **bold** (which may contain links)
  // and standalone inline links.
  const renderParagraph = (text, i) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className="text-gray-800 leading-relaxed mb-5 text-base sm:text-lg">
        {parts.map((p, j) => {
          if (p.startsWith('**') && p.endsWith('**')) {
            return <strong key={j}>{renderInline(p.slice(2, -2), `b${i}-${j}`)}</strong>;
          }
          return <React.Fragment key={j}>{renderInline(p, `t${i}-${j}`)}</React.Fragment>;
        })}
      </p>
    );
  };

  const relatedRestaurants = (post.relatedRestaurants || [])
    .map((id) => ALL_RESTAURANTS.find((r) => r.id === id))
    .filter(Boolean);

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug && (p.category === post.category || p.city === post.city)).slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera</span>
          </Link>
          <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-semibold">← Journal</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <nav className="text-sm text-gray-500 mb-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-teal-600">Home</Link> › <Link to="/blog" className="hover:text-teal-600">Journal</Link> › <span className="text-gray-900">{post.category}</span>
        </nav>

        <article>
          <div className="mb-6 flex items-center gap-2 text-xs">
            <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded font-semibold">{post.category}</span>
            <span className="text-gray-500">{post.readMinutes} min read</span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">{post.publishedAt}</span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">{post.city}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 leading-tight">{post.title}</h1>
          <p className="text-lg text-gray-600 italic mb-8">{post.excerpt}</p>

          {(post.heroImageUrl || post.heroImage) && (
            <img
              src={post.heroImageUrl || `/images/${post.heroImage}`}
              alt={post.heroAlt || post.title}
              className="w-full h-64 sm:h-80 object-cover rounded-xl shadow-sm mb-8"
              data-testid="blog-hero-image"
            />
          )}

          <div className="prose-styled">
            {post.content.split(/\n\n/).map(renderParagraph)}
          </div>

          {relatedRestaurants.length > 0 && (
            <section className="mt-12">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Restaurants featured in this trip</h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {relatedRestaurants.map((r) => (
                  <li key={r.id}>
                    <Link
                      to={`/restaurants/${r.id}`}
                      data-testid={`blog-featured-restaurant-${r.id}`}
                      className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 h-full"
                    >
                      <div className="font-semibold text-gray-900 text-sm">{r.name}</div>
                      <div className="text-xs text-teal-700">{r.cuisine} · {r.city}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="mt-12 pt-8 border-t border-gray-200 text-sm text-gray-500">
            Written by {post.author}. Want to plan an Alberta culinary trip inspired by this article?{' '}
            <Link to="/" className="text-teal-600 hover:underline font-semibold">Start your itinerary →</Link>
          </div>
        </article>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Keep reading</h2>
            <ul className="grid md:grid-cols-3 gap-4">
              {related.map((p) => (
                <li key={p.slug}>
                  <Link to={`/blog/${p.slug}`} className="block bg-white rounded-lg shadow-sm hover:shadow-md p-4 h-full">
                    <div className="text-xs text-teal-700 font-semibold mb-1">{p.category}</div>
                    <div className="font-semibold text-gray-900 text-sm leading-snug">{p.title}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
